import React from 'react';
import { useSelector } from 'react-redux';

import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import AutomaticFeeChangeTag from 'app/shared/components/AutomaticFeeChangeTag';
import { getAutomaticFeeChangeActionType } from 'app/shared/components/AutomaticFeeChangeTag/AutomaticFeeChangeTag.utils';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import { WarningIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import FeeInfoIcon from 'app/shared/componentsRestyled/loanApplication/RecordingFeesData/FeeInfoIcon';
import clsx from 'clsx';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { currencyOptions, FeeCreditSourceLabel } from 'constants/loanFee';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { checkIsInvalidImpound } from 'utils/impounds';
import { getPendingChangesActionType } from 'utils/loanFees';
import { formatNumberToCurrencyString } from 'utils/number';
import { getFieldPendingChangeAuthors } from 'utils/pendingChangesAuthors';

import {
  selectIsEscrowApplicable,
  selectIsSellerPaidFieldsVisible,
  selectLoanFeeCreditsByFeeTypeId,
  selectLoanFeesSummary,
} from 'store/loanApplications/selectors';
import { selectFieldPendingChangeAuthorsByEntityKey } from 'store/loanApplications/selectors/pendingChangesAuthors.selectors';
import { selectFeeTypes } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../LoanFees.testIds';
import AprIndicator from '../../AprIndicator';

import { FEE_FIELD_LABEL } from './FeeTable.constants';
import { getTableStyles, nameAmountTableStyles } from './FeeTable.styles';
import {
  checkIsTotalPayerAmountPresent,
  getAppraisalSurchargeTooltipContent,
  getCombinedAppraisalFeeAmount,
  getFeeFieldPendingChangeAuthors,
  getPayerCellPendingChangesActionType,
} from './FeeTable.utils';

const skeletonItems = Array.from({ length: 3 });

const FeeTable = ({
  isLoading,
  items,
  cdSection,
  isFeeProviderColumnVisible = true,
  isNameAmountSection = false,
  isNonBorrowerPaidFieldsIncluded,
  isWithHeader,
  isTotalView = false,
  labelNameAmountSection,
  amountColumnName,
  isNotEmptyCellsSection,
}) => {
  const isEmpty = !items.length;
  const isSellerPaidFieldsVisible = useSelector(selectIsSellerPaidFieldsVisible);
  const { firstPaymentDate: prepaidInterestCreditFirstPaymentDate } =
    useSelector(selectLoanFeesSummary);
  const feeTypes = useSelector(selectFeeTypes);
  const creditsByFeeTypeId = useSelector(selectLoanFeeCreditsByFeeTypeId);
  const isEscrowApplicable = useSelector(selectIsEscrowApplicable);
  const fieldPendingChangeAuthorsByEntityKey = useSelector(
    selectFieldPendingChangeAuthorsByEntityKey,
  );

  const isFinancedAmountColumnVisible =
    !isTotalView &&
    items.some(
      ({ includeIntoTotalLoanAmount, financedAmount }) =>
        !!includeIntoTotalLoanAmount && !!financedAmount,
    );

  return (
    <TableContainer
      data-testid={LoanFeesTestIds.FEE_TABLE}
      sx={
        isNameAmountSection
          ? nameAmountTableStyles
          : getTableStyles({
              isFeeProviderColumnVisible,
              isFinancedAmountColumnVisible,
              isOpened: false,
              isNonBorrowerPaidFieldsIncluded,
              isEmpty,
            })
      }
    >
      <Table>
        {isWithHeader && (
          <TableHead>
            {isNameAmountSection && (
              <TableRow>
                <TableCell>{labelNameAmountSection}</TableCell>
                <TableCell align="right">{amountColumnName}</TableCell>
              </TableRow>
            )}

            {!isNameAmountSection && (
              <TableRow>
                <TableCell>Fee</TableCell>
                {isNonBorrowerPaidFieldsIncluded && isFeeProviderColumnVisible && (
                  <TableCell>Fee Provider</TableCell>
                )}

                <TableCell align="center" colSpan={2}>
                  <div>Borrower-Paid</div>
                  <div>
                    <span className="text-right">At Closing</span>
                    <span className="text-right">Before Closing</span>
                  </div>
                </TableCell>

                {isNonBorrowerPaidFieldsIncluded && (
                  <>
                    {isSellerPaidFieldsVisible && (
                      <TableCell align="center" colSpan={2}>
                        <div>Seller-Paid</div>
                        <div>
                          <span className="text-right">At Closing</span>
                          <span className="text-right">Before Closing</span>
                        </div>
                      </TableCell>
                    )}

                    <TableCell align="right">Paid By Others</TableCell>
                  </>
                )}

                {isFinancedAmountColumnVisible && (
                  <TableCell align="right">Financed Amount</TableCell>
                )}
              </TableRow>
            )}
          </TableHead>
        )}

        <TableBody>
          {(isLoading ? skeletonItems : items).map(({ index: feeIndex, ...fee } = {}, index) => {
            const isFeeAdded = fee.pendingChangesActionType === PendingChangesActionType.ADDED;
            const isFeeDeleted = fee.pendingChangesActionType === PendingChangesActionType.DELETED;

            const isInvalidImpound =
              !!fee.applicationImpoundId &&
              checkIsInvalidImpound({
                impound: fee,
                prepaidInterestCreditFirstPaymentDate,
                isEscrowApplicable,
              });

            const rowKey = fee.feeTypeId ?? feeIndex ?? index;
            const originalFeeTypeName = feeTypes[fee.feeTypeId]?.name;
            const feeFieldAuthors = getFeeFieldPendingChangeAuthors(
              fieldPendingChangeAuthorsByEntityKey,
              fee,
            );

            const combinedBorrowerPaidAtClosingAmount = getCombinedAppraisalFeeAmount(
              fee.borrowerPaidAtClosingAmount,
              fee.appraisalSurcharges,
              'borrowerPaidAtClosingAmount',
            );
            const combinedBorrowerPaidBeforeClosingAmount = getCombinedAppraisalFeeAmount(
              fee.borrowerPaidBeforeClosingAmount,
              fee.appraisalSurcharges,
              'borrowerPaidBeforeClosingAmount',
            );

            if (isNameAmountSection) {
              return (
                <TableRow
                  className={clsx(
                    (isFeeAdded || isFeeDeleted) && 'table-row-pending',
                    !!fee.pendingChangesActionType &&
                      `table-row-pending-${fee.pendingChangesActionType}`,
                  )}
                  key={rowKey}
                >
                  <TableCell>
                    {isLoading && <Skeleton className="h-24 w-80" variant="rounded" />}

                    {!isLoading && (
                      <PendingChangeWrapper
                        isSmall
                        authors={getFieldPendingChangeAuthors(feeFieldAuthors, 'feeTypeName')}
                        fieldLabel={FEE_FIELD_LABEL.FEE_TYPE_NAME}
                        pendingChangesActionType={getPendingChangesActionType(
                          fee.modifiedFields,
                          'feeTypeName',
                        )}
                      >
                        <>
                          <Typography
                            data-truncated
                            className={clsx('truncate', { 'text-red': isInvalidImpound })}
                            variant="regular14"
                          >
                            {fee.feeTypeName || fee.name}
                          </Typography>
                          <AutomaticFeeChangeTag
                            pendingChangesActionType={getAutomaticFeeChangeActionType(fee)}
                          />
                        </>
                      </PendingChangeWrapper>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    {isLoading && <Skeleton className="float-right h-24 w-80" variant="rounded" />}

                    {!isLoading && (!!fee.amount || isNotEmptyCellsSection) && (
                      <PendingChangeWrapper
                        isSmall
                        authors={getFieldPendingChangeAuthors(feeFieldAuthors, 'amount')}
                        className="float-right"
                        data-is-amount="true"
                        data-is-empty={!fee.amount}
                        fieldLabel={FEE_FIELD_LABEL.AMOUNT}
                        pendingChangesActionType={getPendingChangesActionType(
                          fee.modifiedFields,
                          'amount',
                        )}
                      >
                        <Typography data-truncated className="truncate" variant="regular14">
                          {(fee.feeTypeName || fee.name) === '-'
                            ? ''
                            : formatNumberToCurrencyString(fee.amount || 0, currencyOptions)}
                        </Typography>
                      </PendingChangeWrapper>
                    )}
                    <AprIndicator isIncludedInApr={fee.countTowardsApr} />
                  </TableCell>
                </TableRow>
              );
            }

            return (
              <TableRow
                className={clsx(
                  (isFeeAdded || isFeeDeleted) && 'table-row-pending',
                  `table-row-pending-${fee.pendingChangesActionType}`,
                )}
                key={rowKey}
              >
                <TableCell>
                  {isLoading && <Skeleton className="h-24 w-80" variant="rounded" />}

                  {!isLoading && (
                    <>
                      {!fee.isLocallyAdded && (
                        <PendingChangeWrapper
                          isSmall
                          authors={getFieldPendingChangeAuthors(feeFieldAuthors, 'feeTypeName')}
                          fieldLabel={FEE_FIELD_LABEL.FEE_TYPE_NAME}
                          pendingChangesActionType={getPendingChangesActionType(
                            fee.modifiedFields,
                            'feeTypeName',
                          )}
                        >
                          <>
                            <div className="flex items-center">
                              <Typography data-truncated className="truncate" variant="regular14">
                                {fee.feeTypeName || fee.name}
                              </Typography>
                              {fee.heldPricingPremiumAmount > 0 && (
                                <TooltipWrapper
                                  title={`Rate overage of ${formatNumberToCurrencyString(
                                    fee.heldPricingPremiumAmount,
                                    currencyOptions,
                                  )} is held as a pricing premium and has not been disclosed as a lender credit.`}
                                >
                                  <span className="px-4 cursor flex items-center">
                                    <WarningIcon size={16} />
                                  </span>
                                </TooltipWrapper>
                              )}
                              {!!fee.appraisalSurcharges?.length && (
                                <span data-testid={LoanFeesTestIds.APPRAISAL_SURCHARGE_INFO_ICON}>
                                  <FeeInfoIcon
                                    content={getAppraisalSurchargeTooltipContent(
                                      fee.appraisalSurcharges,
                                    )}
                                  />
                                </span>
                              )}
                            </div>
                            {originalFeeTypeName &&
                              originalFeeTypeName !== (fee.feeTypeName || fee.name) && (
                                <Typography color="text.secondary" variant="regular12">
                                  ({originalFeeTypeName})
                                </Typography>
                              )}
                            <AprIndicator isIncludedInApr={fee.countTowardsApr} />
                            <AutomaticFeeChangeTag
                              pendingChangesActionType={getAutomaticFeeChangeActionType(fee)}
                            />
                            {!!creditsByFeeTypeId[fee.feeTypeId]?.length &&
                              creditsByFeeTypeId[fee.feeTypeId].map(credit => (
                                <Typography
                                  color="text.secondary"
                                  key={credit.loanFeeCreditId}
                                  variant="regular12"
                                >
                                  (Credit:{' '}
                                  {formatNumberToCurrencyString(credit.amount, currencyOptions)}{' '}
                                  from {FeeCreditSourceLabel[credit.creditSource]})
                                </Typography>
                              ))}
                          </>
                        </PendingChangeWrapper>
                      )}

                      {fee.isLocallyAdded && (
                        <PendingChangeWrapper
                          isSmall
                          authors={getFieldPendingChangeAuthors(feeFieldAuthors, 'feeTypeName')}
                          fieldLabel={FEE_FIELD_LABEL.FEE_TYPE_NAME}
                          pendingChangesActionType={getPendingChangesActionType(
                            fee.modifiedFields,
                            'feeTypeName',
                          )}
                        >
                          <>
                            <Typography data-truncated className="truncate" variant="regular14">
                              {fee.feeTypeName || fee.name}
                            </Typography>
                            <AprIndicator isIncludedInApr={fee.countTowardsApr} />
                          </>
                        </PendingChangeWrapper>
                      )}
                    </>
                  )}
                </TableCell>

                {isNonBorrowerPaidFieldsIncluded && isFeeProviderColumnVisible && (
                  <TableCell data-fee-label="Fee Provider">
                    {isLoading && <Skeleton className="h-24 w-80" variant="rounded" />}

                    {!isLoading && (
                      <PendingChangeWrapper
                        isSmall
                        authors={getFieldPendingChangeAuthors(feeFieldAuthors, 'feeProviderName')}
                        fieldLabel={FEE_FIELD_LABEL.FEE_PROVIDER_NAME}
                        pendingChangesActionType={getPendingChangesActionType(
                          fee.modifiedFields,
                          'feeProviderName',
                        )}
                      >
                        <Typography data-truncated className="truncate" variant="regular14">
                          {fee.feeProviderName}
                        </Typography>
                      </PendingChangeWrapper>
                    )}
                  </TableCell>
                )}

                <TableCell align="center" colSpan={2}>
                  <div>
                    {isTotalView && (
                      <>
                        <span
                          data-fee-label="Borrower-Paid · At Closing"
                          id="total-at-closing-amount"
                        >
                          {isLoading && (
                            <Skeleton className="float-right h-24 w-80" variant="rounded" />
                          )}

                          {!isLoading && isNotEmptyCellsSection && (
                            <PendingChangeWrapper
                              isSmall
                              authors={getFieldPendingChangeAuthors(
                                feeFieldAuthors,
                                'totalAtClosingAmount',
                              )}
                              className="float-right"
                              data-is-amount="true"
                              data-is-empty={!fee.totalAtClosingAmount}
                              fieldLabel={FEE_FIELD_LABEL.TOTAL_AT_CLOSING_AMOUNT}
                              pendingChangesActionType={getPendingChangesActionType(
                                fee.modifiedFields,
                                'totalAtClosingAmount',
                              )}
                            >
                              <Typography data-truncated className="truncate" variant="regular14">
                                {formatNumberToCurrencyString(
                                  fee.totalAtClosingAmount || 0,
                                  currencyOptions,
                                )}
                              </Typography>
                            </PendingChangeWrapper>
                          )}
                        </span>

                        <span
                          data-fee-label="Borrower-Paid · Before Closing"
                          id="total-before-closing-amount"
                        >
                          {isLoading && (
                            <Skeleton className="float-right h-24 w-80" variant="rounded" />
                          )}

                          {!isLoading && isNotEmptyCellsSection && (
                            <PendingChangeWrapper
                              isSmall
                              authors={getFieldPendingChangeAuthors(
                                feeFieldAuthors,
                                'totalBeforeClosingAmount',
                              )}
                              className="float-right"
                              data-is-amount="true"
                              data-is-empty={!fee.totalBeforeClosingAmount}
                              fieldLabel={FEE_FIELD_LABEL.TOTAL_BEFORE_CLOSING_AMOUNT}
                              pendingChangesActionType={getPendingChangesActionType(
                                fee.modifiedFields,
                                'totalBeforeClosingAmount',
                              )}
                            >
                              <Typography data-truncated className="truncate" variant="regular14">
                                {formatNumberToCurrencyString(
                                  fee.totalBeforeClosingAmount || 0,
                                  currencyOptions,
                                )}
                              </Typography>
                            </PendingChangeWrapper>
                          )}
                        </span>
                      </>
                    )}

                    {!isTotalView && (
                      <>
                        <span data-fee-label="Borrower-Paid · At Closing">
                          {isLoading && (
                            <Skeleton className="float-right h-24 w-80" variant="rounded" />
                          )}

                          {!isLoading && (
                            <PendingChangeWrapper
                              isSmall
                              authors={getFieldPendingChangeAuthors(
                                feeFieldAuthors,
                                'borrowerPaidAtClosingAmount',
                              )}
                              className="float-right"
                              data-is-amount="true"
                              data-is-empty={!combinedBorrowerPaidAtClosingAmount}
                              fieldLabel={FEE_FIELD_LABEL.BORROWER_PAID_AT_CLOSING_AMOUNT}
                              pendingChangesActionType={getPendingChangesActionType(
                                fee.modifiedFields,
                                'borrowerPaidAtClosingAmount',
                              )}
                            >
                              <Typography data-truncated className="truncate" variant="regular14">
                                {formatNumberToCurrencyString(
                                  combinedBorrowerPaidAtClosingAmount,
                                  currencyOptions,
                                )}
                              </Typography>
                            </PendingChangeWrapper>
                          )}
                        </span>

                        <span data-fee-label="Borrower-Paid · Before Closing">
                          {isLoading && (
                            <Skeleton className="float-right h-24 w-80" variant="rounded" />
                          )}

                          {!isLoading && (
                            <PendingChangeWrapper
                              isSmall
                              authors={getFieldPendingChangeAuthors(
                                feeFieldAuthors,
                                'borrowerPaidBeforeClosingAmount',
                              )}
                              className="float-right"
                              data-is-amount="true"
                              data-is-empty={!combinedBorrowerPaidBeforeClosingAmount}
                              fieldLabel={FEE_FIELD_LABEL.BORROWER_PAID_BEFORE_CLOSING_AMOUNT}
                              pendingChangesActionType={getPendingChangesActionType(
                                fee.modifiedFields,
                                'borrowerPaidBeforeClosingAmount',
                              )}
                            >
                              <Typography data-truncated className="truncate" variant="regular14">
                                {formatNumberToCurrencyString(
                                  combinedBorrowerPaidBeforeClosingAmount,
                                  currencyOptions,
                                )}
                              </Typography>
                            </PendingChangeWrapper>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                </TableCell>

                {isNonBorrowerPaidFieldsIncluded && (
                  <>
                    {isSellerPaidFieldsVisible && (
                      <TableCell align="center" colSpan={2}>
                        <div>
                          <span
                            data-fee-label="Seller-Paid · At Closing"
                            data-testid={
                              checkIsTotalPayerAmountPresent({
                                fee,
                                field: 'sellerPaidAtClosingAmount',
                                isTotalView,
                              })
                                ? LoanFeesTestIds.getTotalSectionSellerAtClosingId(cdSection)
                                : null
                            }
                          >
                            {isLoading && (
                              <Skeleton className="float-right h-24 w-80" variant="rounded" />
                            )}

                            {!isLoading && (
                              <PendingChangeWrapper
                                isSmall
                                authors={getFieldPendingChangeAuthors(
                                  feeFieldAuthors,
                                  'sellerPaidAtClosingAmount',
                                )}
                                className="float-right"
                                data-is-empty={!fee.sellerPaidAtClosingAmount}
                                fieldLabel={FEE_FIELD_LABEL.SELLER_PAID_AT_CLOSING_AMOUNT}
                                pendingChangesActionType={getPayerCellPendingChangesActionType({
                                  fee,
                                  field: 'sellerPaidAtClosingAmount',
                                  isTotalView,
                                })}
                              >
                                <Typography data-truncated className="truncate" variant="regular14">
                                  {formatNumberToCurrencyString(
                                    fee.sellerPaidAtClosingAmount || 0,
                                    currencyOptions,
                                  )}
                                </Typography>
                              </PendingChangeWrapper>
                            )}
                          </span>

                          <span
                            data-fee-label="Seller-Paid · Before Closing"
                            data-testid={
                              checkIsTotalPayerAmountPresent({
                                fee,
                                field: 'sellerPaidBeforeClosingAmount',
                                isTotalView,
                              })
                                ? LoanFeesTestIds.getTotalSectionSellerBeforeClosingId(cdSection)
                                : null
                            }
                          >
                            {isLoading && (
                              <Skeleton className="float-right h-24 w-80" variant="rounded" />
                            )}

                            {!isLoading && (
                              <PendingChangeWrapper
                                isSmall
                                authors={getFieldPendingChangeAuthors(
                                  feeFieldAuthors,
                                  'sellerPaidBeforeClosingAmount',
                                )}
                                className="float-right"
                                data-is-empty={!fee.sellerPaidBeforeClosingAmount}
                                fieldLabel={FEE_FIELD_LABEL.SELLER_PAID_BEFORE_CLOSING_AMOUNT}
                                pendingChangesActionType={getPayerCellPendingChangesActionType({
                                  fee,
                                  field: 'sellerPaidBeforeClosingAmount',
                                  isTotalView,
                                })}
                              >
                                <Typography data-truncated className="truncate" variant="regular14">
                                  {formatNumberToCurrencyString(
                                    fee.sellerPaidBeforeClosingAmount || 0,
                                    currencyOptions,
                                  )}
                                </Typography>
                              </PendingChangeWrapper>
                            )}
                          </span>
                        </div>
                      </TableCell>
                    )}

                    <TableCell
                      align="right"
                      data-fee-label="Paid By Others"
                      data-testid={
                        checkIsTotalPayerAmountPresent({
                          fee,
                          field: 'paidByOthersAmount',
                          isTotalView,
                        })
                          ? LoanFeesTestIds.getTotalSectionPaidByOthersId(cdSection)
                          : null
                      }
                    >
                      {isLoading && (
                        <Skeleton className="float-right h-24 w-80" variant="rounded" />
                      )}

                      {!isLoading && (
                        <PendingChangeWrapper
                          isSmall
                          authors={getFieldPendingChangeAuthors(
                            feeFieldAuthors,
                            'paidByOthersAmount',
                          )}
                          className="float-right"
                          data-is-empty={!fee.paidByOthersAmount}
                          fieldLabel={FEE_FIELD_LABEL.PAID_BY_OTHERS_AMOUNT}
                          pendingChangesActionType={getPayerCellPendingChangesActionType({
                            fee,
                            field: 'paidByOthersAmount',
                            isTotalView,
                          })}
                        >
                          <Typography data-truncated className="truncate" variant="regular14">
                            {formatNumberToCurrencyString(
                              fee.paidByOthersAmount || 0,
                              currencyOptions,
                            )}
                          </Typography>
                        </PendingChangeWrapper>
                      )}
                    </TableCell>
                  </>
                )}

                {isFinancedAmountColumnVisible && (
                  <TableCell
                    align="right"
                    className="financed-amount-cell"
                    data-fee-label="Financed Amount"
                  >
                    {isLoading && <Skeleton className="float-right h-24 w-80" variant="rounded" />}

                    {!isLoading && (
                      <PendingChangeWrapper
                        isSmall
                        authors={getFieldPendingChangeAuthors(feeFieldAuthors, 'financedAmount')}
                        className="float-right"
                        data-is-empty={!fee.financedAmount}
                        fieldLabel={FEE_FIELD_LABEL.FINANCED_AMOUNT}
                        pendingChangesActionType={getPendingChangesActionType(
                          fee.modifiedFields,
                          'financedAmount',
                        )}
                      >
                        <Typography data-truncated className="truncate" variant="regular14">
                          {formatNumberToCurrencyString(fee.financedAmount, currencyOptions)}
                        </Typography>
                      </PendingChangeWrapper>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {isEmpty && !isLoading && (
        <NoAvailableMessage message="Sorry, but there is no data available for this section" />
      )}
    </TableContainer>
  );
};

FeeTable.propTypes = {
  ...WithLoadingPropTypes,
  items: PropTypes.array.isRequired,
  isNonBorrowerPaidFieldsIncluded: PropTypes.bool.isRequired,
  isWithHeader: PropTypes.bool.isRequired,
  cdSection: PropTypes.string,
  isTotalView: PropTypes.bool,
  isNameAmountSection: PropTypes.bool,
  amountColumnName: PropTypes.string,
  isFeeProviderColumnVisible: PropTypes.bool,
  labelNameAmountSection: PropTypes.string,
};

export default FeeTable;
